import React, { useEffect, useState } from 'react';
import { Form, Schema, Stack, Col, Grid, Row, IconButton, Divider, FormInstance, NumberInput, Checkbox, SelectPicker } from 'rsuite';
import { useNavigate } from 'react-router-dom';
import { BaseErrorModel, BaseResponseModel, PageState } from '@/models/core.model';
import { ObjectStorageFileCategory } from '@/models/storage.model';
import PageContent from '@/components/PageContent';
import PageNextIcon from '@rsuite/icons/PageNext';
import PagePreviousIcon from '@rsuite/icons/PagePrevious';
import ErrorMessage from '@/components/ErrorMessage';
import { AxiosError } from 'axios';
import ImageUploader from '@/components/ImageUploader';
import { StoryChapterMedia, UpsertStoryChapterMedia } from '@/models/story-chapter-media.model';
import {upsertStoryChapterMedia } from '@/services/story-chapter-media.service';
import Textarea from '@/components/Textarea';
import { defaultLanguage, languages } from '@/models/language.model';
import AudioUploader from '@/components/AudioUploader';

const storyChapterSchema = Schema.Model<UpsertStoryChapterMedia>({
  id: Schema.Types.NumberType(),
  text: Schema.Types.StringType().isRequired('This field is required.'),
  imageUrl: Schema.Types.StringType(),
  voiceUrl: Schema.Types.StringType(),
  language: Schema.Types.StringType().isRequired('This field is required.'),
  order: Schema.Types.NumberType().isRequired('This field is required.'),
  isIntro: Schema.Types.BooleanType().isRequired('This field is required.'),
  chapterId: Schema.Types.NumberType().isRequired('This field is required.'),
});


const emptyModel: UpsertStoryChapterMedia = {
  id: undefined,
  text: '',
  imageUrl: '',
  voiceUrl: '',
  language: defaultLanguage,
  order: undefined,
  isIntro: false,
  chapterId: undefined,
};

interface StoryChapterMediaFormProps{
  formObject?: StoryChapterMedia;
  pageState: PageState,
  chapterId?: number;
};

const StoryChapterMediaForm: React.FC<StoryChapterMediaFormProps> = ({formObject, pageState, chapterId}) => {
  const [formValue, setFormValue] = useState<UpsertStoryChapterMedia>(emptyModel);
  const [formError, setFormError] = useState<Record<string, any>>({});
  const [errorModel, setErrorModel] = useState<BaseErrorModel | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  
  const nav = useNavigate();
  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);

  useEffect(() => {
    if(pageState == PageState.New){
      setFormValue({...emptyModel, chapterId: chapterId});
      return;
    }

    if (formObject) {
      setFormValue(formObject);
    } else {
      setFormValue(emptyModel);
    }
  }, [formObject]);

  const handleSubmit = async () => {
    if (formRef.current && !formRef.current.check()) {
      console.log("errors:", formError);
      return;
    }
    setErrorModel(undefined);
    setLoading(true);
    try {
      const result: BaseResponseModel<StoryChapterMedia> = await upsertStoryChapterMedia(formValue);
      if (result.success) {
        nav(`/stories/chapters/${result.result.chapterId}`, {state:{tab: 'story-chapter-medias'}});
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    }finally{
      setLoading(false);
    }
  };

  const handleFormChange = (value: Record<string, any>) => {
    setFormValue(prevFormValue => ({
      ...prevFormValue,
      ...value
    }));
  };

  return (
    <PageContent>
      <Form name='story-chapter-media-form' fluid
        ref={formRef}
        onChange={handleFormChange}
        onCheck={setFormError}
        formValue={formValue}
        model={storyChapterSchema}
        disabled={loading}
      >
        <Grid w='100%' fluid>
          <Row>
            <Col span={{xs:24, xl:8}} style={{textAlign: 'center'}}>
              <Form.Group>
                <Form.Label>Image</Form.Label>              
                <Form.Control
                  accepter={ImageUploader}
                  name="imageUrl"
                  height="300px"
                  url={formValue.imageUrl}
                  objectStorageCategory={ObjectStorageFileCategory.StoryChapterMedia}
                  onChange={url => setFormValue(pre => ({...pre, imageUrl: url}))}                
                />            
              </Form.Group>            
            </Col> 
            <Col span={{xs:24, xl:16}} >
              <Row>
                <Col span={{xs:24}} >
                  <Form.Group>
                    <Form.Label>Voice</Form.Label>
                    <Form.Control
                      accepter={AudioUploader}
                      name="voiceUrl"
                      url={formValue.voiceUrl}
                      objectStorageCategory={ObjectStorageFileCategory.StoryChapterBanner}
                      onChange={url => setFormValue(pre => ({...pre, voiceUrl: url}))}
                      downloadable              
                    />   
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Text</Form.Label>
                    <Form.Control name="text" accepter={Textarea} rows={4} /> 
                  </Form.Group>
                </Col>
              </Row>
              <Row>             
                <Col span={{xs:24, lg:6}} >
                  <Form.Group>
                    <Form.Label>Language</Form.Label>
                    <Form.Control name="language" accepter={SelectPicker} data={languages} block/>
                  </Form.Group>
                </Col>
                <Col span={{xs:24, lg:6}} >
                  <Form.Group>
                    <Form.Label>Order</Form.Label>
                    <Form.Control name="order" accepter={NumberInput}/>
                  </Form.Group>
                </Col>
                <Col span={{xs:24, lg:6}} >
                  <Form.Group>
                    <Form.Label>Is Intro</Form.Label>
                    <Form.Control name="isIntro" accepter={Checkbox} 
                      checked={!!formValue.isIntro} 
                      onChange={(val, chk) => setFormValue(pre => ({ ...pre, isIntro: chk }))}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Col>
          </Row>          
        </Grid>
      </Form>
      <Divider />
      {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom:20 }}></ErrorMessage>}

      <Stack justifyContent="space-between">
        <IconButton icon={<PagePreviousIcon />} onClick={() => nav(-1)}>
          Back
        </IconButton>
        <IconButton
          icon={<PageNextIcon />}
          placement="right"
          appearance="primary"
          onClick={handleSubmit}
          loading={loading}
        >
          Save
        </IconButton>
      </Stack>
    </PageContent>
  );
};

export default StoryChapterMediaForm;

